<template>
    <div class="container-fluid job-list-container">
        <div
            v-if="isDayEarlyStart"
            class="row early-start-row"
        >
            <i class="fa fa-sun" />
            <span>Early Start</span>
        </div>
        <div
            class="row"
            v-if="timeSlots.length && !loading"
        >
            <job-item 
                v-for="timeSlot of timeSlots"
                :key="timeSlot.id"
                :time-slot="timeSlot"
            />
        </div>
        <div
            class="row"
            v-if="timeSlots.length == 0 && !loading"
        >
            <div class="col-sm-12 text-center">
                <span>No jobs for this day</span>
            </div>
        </div>
        <div
            v-if="isDayOvernight"
            class="row overnight-row"
        >
            <i class="fa fa-moon" />
            <span>Overnight / Late Night</span>
        </div>
        <div class="row">
            <div
                class="col-sm-12 text-center"
                v-if="loading"
            >
                <span>Loading <i class="fa fa-spinner fa-spin" /></span>
            </div>
        </div>

        <div class="row">
            <div class="col-sm-12 text-center">
                <button
                    class="btn new-job-btn floating-new-btn"
                    @click="newJob"
                >
                    <i class="fa fa-plus" />
                </button>
            </div>

            <button
                class="btn floating-refresh-btn"
                @click="refresh"
            >
                <i
                    class="fas fa-sync-alt"
                    :class="{'fa-spin' : loading}"
                />
            </button>
            <JobRoutesMapButton
                :user="user"
                :events="timeSlots"
                engineer-mode
            />
        </div>

        <modal
            ref="newJobModal"
            title="New Job"
            class="new-job-modal"
        >
            <template v-slot:default>
                <new-job
                    ref="newJob"
                    @created="jobChanged"
                    @deleted="jobChanged"
                />
            </template>
        </modal>

        <nav class="job-date-nav col-sm-12">
            <button
                class="btn btn-info prev-day-btn"
                @click="prevDay()"
            >
                <i class="fa fa-chevron-left" /> Prev Day
            </button>
            <button
                class="btn btn-primary today-btn"
                @click="today()"
            >
                Today
            </button>
            <button
                class="btn btn-info next-day-btn"
                @click="nextDay()"
            >
                Next Day
                <i class="fa fa-chevron-right" />
            </button>
        </nav>
    </div>
</template>

<script>
import TimeSlotsService from "../../services/TimeSlotsService";
import AuthService from "../../services/AuthService";
import Modal from '../modals/ModalComponent';
import NewJob from '../NewJobComponent';
import JobItem from './JobListItemComponent';
import { mapMutations } from 'vuex';
import CalendarEventsService from "../../services/CalendarEventsService";
import JobRoutesMapButton from "../JobRoutesMapButton.vue";
import {formatTimeslot} from "../../store/resourceCalendar";
const dateFormat = 'YYYY-MM-DD';
const storageKey = 'engineer_jobs_date';

const moment = window.moment;

export default {
    components: {
        JobRoutesMapButton,
        Modal,
        NewJob,
        JobItem,
    },
    data: function() {
        return {
            timeSlots: [],
            user: {},
            loadingTimeslots: false,
            loadingEvents: false,
            events: [],
        };
    },

    computed: {
        now: () => moment(),
        loading: () => this.loadingTimeslots || this.loadingEvents,
        startTime () {
            if (this.$route.query.date) {
                return window.moment(this.$route.query.date).startOf('day');
            }
            const storageDate = window.localStorage.getItem(storageKey);
            if (storageDate) {
                return window.moment(storageDate).startOf('day');
            }
            return moment().startOf("day");
        },
        endTime() {
            return this.startTime.clone().endOf('day');
        },
        isDayEarlyStart() {
            return this.events.some(e => e.type === 'Early Start');
        },
        isDayOvernight() {
            return this.events.some(e => e.type === 'Overnight');
        },
    },

    watch: {
        startTime: {
            handler: function() {
                this.unsubscribe();
                const isToday = this.startTime.isSame(window.moment(), 'day');
                this.setPageTitle('Jobs ' + this.startTime.format('Do MMM YYYY') + (isToday ? ' (Today)' : ''));
                this.getTimeSlots();
                this.getEvents();
                window.localStorage.setItem(storageKey, this.startTime.format(dateFormat));
                this.subscribe();
            },
            immediate: true,
            deep: true,
        }
    },
    mounted: function() {
        AuthService.getUser().then(user => {
            this.user = user;
            this.getTimeSlots();
            this.getEvents();
        });
    },

    beforeDestroy() {
        this.unsubscribe();
    },
    methods: {
        ...mapMutations(['setPageTitle']),
        getTimeSlots: function() {
            if (this.loadingTimeslots)  {
                return;
            }
            this.timeSlots = [];
            if(!this.user.id) {
                return;
            }
            this.loadingTimeslots = true;
            TimeSlotsService.search({
                include: ["job", "job.customer", "job.site.address"],
                "user_id": this.user.id,
                "start": this.startTime.format('YYYY-MM-DD HH:mm:ss'),
                "end": this.endTime.format('YYYY-MM-DD HH:mm:ss')
            }).then(
                xhr => {
                    this.loadingTimeslots = false;
                    this.timeSlots = xhr.data.map(event => formatTimeslot(event));
                },
                () => {
                    alert("Error: Failed to load jobs, please try again.");
                }
            );
        },
        getEvents: function() {
            if (this.loadingEvents) {
                return;
            }
            this.events = [];
            if(!this.user.id) {
                return;
            }
            this.loadingEvents = true;
            CalendarEventsService.fetchEvents({
                "user_id": this.user.id,
                "start": this.startTime.format('YYYY-MM-DD HH:mm:ss'),
                "end": this.startTime.clone().endOf('day').format('YYYY-MM-DD HH:mm:ss')
            }).then(
                xhr => {
                    this.loadingEvents = false;
                    this.events = xhr.data;
                },
                () => {
                    alert("Error: Failed to load events, please try again.");
                }
            );
        },

        prevDay: function() {
            this.goToDate(this.startTime.clone().subtract(1, 'day'));
        },

        nextDay: function() {
            this.goToDate(this.startTime.clone().add(1, 'day'));
        },

        today: function() {
            this.goToDate(window.moment());
        },

        goToDate(date) {
            this.$router.push({query: {date: date.format(dateFormat)}});
        },

        newJob() {
            this.$refs.newJobModal.open();
        },

        jobChanged() {
            this.$refs.newJob.clear();
            this.$refs.newJobModal.close();
            this.getTimeSlots();
            this.getEvents();
        },

        subscribe() {
            const jobsChannel = window.Echo.channel('jobs');
            jobsChannel.listen('JobUpdated', (event) => {
                if (event.start && moment(event.start).isSame(this.startTime, 'day')) {
                    this.getTimeSlots();
                }
            })
            const eventsChannel = window.Echo.channel('events');
            eventsChannel.listen('CalendarEventChanged', (event) => {
                console.log(event)
                if (event.start && moment(event.start).isSame(this.startTime, 'day')) {
                    this.getEvents();
                }
            })
        },

        unsubscribe() {
            window.Echo.leave('jobs');
            window.Echo.leave('events');
        },

        refresh() {
            this.getTimeSlots();
            this.getEvents();
        },
    }
};
</script>

<style lang="scss">
.early-start-row, .overnight-row {
    border: 1px solid;
    text-align: center;
    margin: 1rem 0;
    border-radius: 2px;
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: center;
    padding: 1rem !important;
    font-size: 1.2rem;
}
.early-start-row {
    background-color: #fff3cd;
    border-color: #ffeeba;
    color: #5d4502;

    .fa {
        color: #ecae00;
    }
}

.overnight-row {
    background-color: #5150cc;
    border-color: #3d3c9d;
    color: #fff;
}
</style>



// WEBPACK FOOTER //
// resources/assets/js/components/engineer/JobListComponent.vue